import { extend } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { DropdownColumnOption } from '@main-application/boards/boards-view/boards-cell/cell-types/dropdown-cell/dropdown-cell.component';
import { InspectionType } from '@main-application/inspections/models/rest-inspections-model.interface';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import {
  PaymentStatus,
  RestTurnoverDataModel,
  RestTurnoverTaskModel,
  TotalCostStatus,
} from '@shared/interfaces/turnover.interface';

import {
  BoardColumnType,
  BoardLayoutType,
  BoardOutputBehaviour,
  BoardOutputType,
  BoardProgressType,
  BoardSourceType,
  BoardType,
  ScheduleCellStatus,
} from './board.enums';
import { IBoardGroupsConfig } from './borad-groups.interface';
import { CellValueHelper } from '../services/cell-value.helper';
import { QueryType, ScheduleByStatusFilter, TurnStepScheduleStatus } from '../services/turn-step-schedule.service';

export class RestTurnStepScheduleBaseModel {
  turnoverDateAvailable: Date;
  turnoverDateShowable: Date;
  dateCompleted: Date;
  originalDueDate: Date;
}

export class RestTurnStepScheduleModel extends RestTurnStepScheduleBaseModel {
  id: number;
  turnoverId: number;
  boardColumnUID: string;
  boardLayoutsId: number;
  workflowStepId: number;
  assignee: number;
  //assigneeId: number; //why is it here?
  notifyAssignee: boolean;
  dueDate: Date;
  status: ScheduleCellStatus;
  note: string;
  sortOrder: number;
  amount?: number;
  totalCostStatus?: TotalCostStatus;
  paymentStatus?: PaymentStatus;
  inspectionId?: number;
  inspectionType?: InspectionType;
  inspectionTemplateId?: number;
  inspectionItemsCompleted?: number;
  inspectionItemsTotal?: number;

  notifyWhenDoneUserIds?: number[];
  notifyWhenDone?: boolean;

  declinedAt?: string;
  declinedReason?: string;

  pushToPMS?: boolean;
  ticketTopicId?: number;
  turnoverData: RestTurnoverDataModel;

  ticketId?: number;
  scheduleAppointmentIsRequired: boolean;
  appointmentConfirmed: boolean;
  appointmentDate?: Date;
  appointmentTime?: Date;
  appointmentNotes?: string;
  requiresAcceptance?: boolean;
  poNumber?: string;

  dynamicTicketTypeId?: number;
}

export class RestBoardModel {
  id: number;
  name: string;
  allPortfolios: boolean;
  selectedPortfolioIds?: string;
  excludedProperties?: string;
  boardType: BoardType;
  boardLayoutType: BoardLayoutType;
  boardColumnsConfig: string;
  progressType: BoardProgressType;
  datasource: BoardSourceType;
  projectTypes: number[];
  outputAction: BoardOutputType;
  outputBehaviour: BoardOutputBehaviour;
  boardCellColor: string;
  extendedConfigJson?: string;
  showArchivedTurnovers?: boolean;
  dontCreateTickets?: boolean;
  defaultScheduleDynamicTicketTypeId?: number | null;
  defaultCostDynamicTicketTypeId?: number | null;
  poNumberIsAvailable?: boolean;
  dataSourceChangesProcessedOn?: Date | null;

  constructor(board: Board) {
    this.id = board.id;
    this.name = board.name;
    this.progressType = board.progressType;
    this.datasource = board.datasource;
    this.projectTypes = board.projectTypes;
    this.boardType = board.boardType;
    this.boardLayoutType = board.boardLayoutType;
    this.outputAction = board.outputAction;
    this.outputBehaviour = board.outputBehaviour;
    this.boardCellColor = board.boardCellColor;
    this.allPortfolios = board.allPortfolios;
    this.extendedConfigJson = JSON.stringify(board.extendedConfigParsed);
    this.selectedPortfolioIds = board.selectedPortfolioIds?.length ? board.selectedPortfolioIds.join(',') : undefined;
    this.excludedProperties = board.excludedPropertyIds?.length ? board.excludedPropertyIds.join(',') : undefined;
    this.boardColumnsConfig = JSON.stringify(
      board.columns.filter(e => e.type !== BoardColumnType.Unselected && e.type !== BoardColumnType.BulkModeSelectRow)
    );
    this.showArchivedTurnovers = board.showArchivedTurnovers ?? false;
    this.dontCreateTickets = !board.defaultScheduleDynamicTicketTypeId && !board.defaultCostDynamicTicketTypeId;
    this.defaultScheduleDynamicTicketTypeId = board.defaultScheduleDynamicTicketTypeId ?? null;
    this.defaultCostDynamicTicketTypeId = board.defaultCostDynamicTicketTypeId ?? null;
    this.poNumberIsAvailable = !!board.poNumberIsAvailable;
  }
}

function isBoardResponse(entity: any): entity is RestBoardModel {
  return (
    typeof entity.datasource === 'number' &&
    typeof entity.id === 'number' &&
    typeof entity.boardColumnsConfig === 'string' &&
    typeof entity.name === 'string'
  );
}

export interface BoardConfiguration {
  id: number;
  name: string;
  allPortfolios: boolean;
  selectedPortfolioIds?: number[];
  excludedPropertyIds?: number[];
  boardType: BoardType;
  boardLayoutType: BoardLayoutType;
  progressType: BoardProgressType;
  datasource: BoardSourceType;
  projectTypes: number[];
  outputAction: BoardOutputType;
  outputBehaviour: BoardOutputBehaviour;
  showArchivedTurnovers?: boolean;
  extendedConfigParsed: IBoardGroupsConfig;
  boardCellColor: string;
  dontCreateTickets?: boolean;
  defaultScheduleDynamicTicketTypeId?: number | null;
  defaultCostDynamicTicketTypeId?: number | null;
  poNumberIsAvailable: boolean;
}

export interface ColumnOptions {
  editable?: boolean;
  name?: string;
  autoNumber?: boolean;
  inspectionTemplateId?: number;
  dynamicTicketTypeId?: number;
  options?: DropdownColumnOption[];
}

export class Board implements BoardConfiguration {
  id: number;
  name: string;
  allPortfolios: boolean;
  selectedPortfolioIds?: number[];
  excludedPropertyIds?: number[];
  boardType: BoardType;
  boardLayoutType: BoardLayoutType;
  progressType: BoardProgressType;
  datasource: BoardSourceType;
  projectTypes: number[];
  outputAction: BoardOutputType;
  outputBehaviour: BoardOutputBehaviour;
  extendedConfigJson: string;
  extendedConfigParsed: IBoardGroupsConfig;
  boardCellColor: string;
  showArchivedTurnovers?: boolean;
  columns: BoardColumn[] = [];
  dontCreateTickets?: boolean;
  defaultCostDynamicTicketTypeId?: number;
  defaultScheduleDynamicTicketTypeId?: number;
  poNumberIsAvailable: boolean;
  dataSourceChangesProcessedOn?: Date | null;

  constructor(config: BoardConfiguration | RestBoardModel, newColumns?: BoardColumn) {
    if (isBoardResponse(config)) {
      const columns = JSON.parse(config.boardColumnsConfig);
      if (Array.isArray(columns)) {
        if (newColumns) columns.push(newColumns);
        this.columns = columns.filter(e => isBoardColumn(e));
        this.columns.forEach(c => (c.id = c.id ?? uuidv4()));

        const propertyColumnIndex = this.columns.findIndex(c => c.type == BoardColumnType.PropertyName);
        let unitColumnIndex = this.columns.findIndex(c => c.type == BoardColumnType.Unit);

        if (propertyColumnIndex === -1 || propertyColumnIndex !== unitColumnIndex + 1) {
          // make Property columns always next after Unit
          if (propertyColumnIndex !== -1) {
            this.columns.splice(propertyColumnIndex, 1);
          }
          unitColumnIndex = this.columns.findIndex(c => c.type == BoardColumnType.Unit);

          this.columns.splice(unitColumnIndex + 1, 0, {
            id: uuidv4(),
            name: 'Property',
            type: BoardColumnType.PropertyName,
            editable: false,
            visible: true,
            frozen: false,
          });
        }
      } else {
        this.initDefaultColumns();
      }
      this.id = config.id;
      this.name = config.name;
      this.progressType = config.progressType;
      this.boardType = config.boardType;
      this.boardLayoutType = config.boardLayoutType;
      this.datasource = config.datasource;
      this.projectTypes = config.projectTypes;
      this.outputAction = config.outputAction;
      this.outputBehaviour = config.outputBehaviour;
      this.selectedPortfolioIds = config.selectedPortfolioIds
        ? config.selectedPortfolioIds.split(',').map(Number)
        : undefined;
      this.excludedPropertyIds = config.excludedProperties
        ? config.excludedProperties.split(',').map(Number)
        : undefined;
      this.allPortfolios = config.allPortfolios;
      this.extendedConfigJson = config.extendedConfigJson;
      this.extendedConfigParsed = config.extendedConfigJson ? JSON.parse(config.extendedConfigJson) : null;
      this.boardCellColor = config.boardCellColor ?? '#ffffff';
      this.showArchivedTurnovers = config.showArchivedTurnovers ?? false;
      this.dontCreateTickets = config.dontCreateTickets;
      this.poNumberIsAvailable = config.poNumberIsAvailable;
      this.dataSourceChangesProcessedOn = config.dataSourceChangesProcessedOn;
      this.defaultCostDynamicTicketTypeId = config.defaultCostDynamicTicketTypeId || null;
      this.defaultScheduleDynamicTicketTypeId = config.defaultScheduleDynamicTicketTypeId || null;
    } else {
      extend(this, config);
      this.initDefaultColumns();
    }
  }

  addColumn(
    name: string,
    type: BoardColumnType,
    editable = true,
    workflowStep = null,
    visible = true,
    frozen = false,
    poisition?: number
  ): string {
    const id = uuidv4();
    const column = {
      id,
      name,
      type,
      workflowStep,
      editable,
      visible,
      frozen,
    };
    if (poisition === undefined) {
      this.columns.push(column);
    } else {
      this.columns.splice(poisition, 0, column);
    }
    return id;
  }

  private initDefaultColumns() {
    const isProjectBoard = this.boardLayoutType === BoardLayoutType.ProjectsBoard;
    this.addColumn('Unit', BoardColumnType.Unit, false, null, true, true);
    this.addColumn('Property', BoardColumnType.PropertyName, false, null, true, true);

    switch (this.boardType) {
      case BoardType.MakeReady:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Move out', BoardColumnType.MoveOut, false);
        this.addColumn('Move in', BoardColumnType.MoveIn, false);
        this.addColumn('Avail', BoardColumnType.Available, true);
        this.addColumn('Trash out', BoardColumnType.Simple);
        this.addColumn('Paint', BoardColumnType.Simple);
        this.addColumn('Flooring', BoardColumnType.Simple);
        this.addColumn('Clean', BoardColumnType.Simple);
        break;
      case BoardType.Reno:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Move out', BoardColumnType.MoveOut, false);
        this.addColumn('Move in', BoardColumnType.MoveIn, false);
        this.addColumn('Avail', BoardColumnType.Available, true);
        this.addColumn('Demo', BoardColumnType.Simple);
        this.addColumn('Paint', BoardColumnType.Simple);
        this.addColumn('Flooring', BoardColumnType.Simple);
        this.addColumn('Trimout', BoardColumnType.Simple);
        this.addColumn('Appliances', BoardColumnType.Simple);
        this.addColumn('Final clean', BoardColumnType.Simple);
        break;
      case BoardType.Marketing:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Type', BoardColumnType.UnitType, false);
        this.addColumn('Finish', BoardColumnType.Finish, false);
        this.addColumn('Move out', BoardColumnType.MoveOut, false);
        this.addColumn('Avail', BoardColumnType.Available, true);
        this.addColumn('Adv.Rent', BoardColumnType.AdvertisedRent, false);
        this.addColumn('Market Rent', BoardColumnType.MarketRent, false);
        break;
      case BoardType.Eviction:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('NTV', BoardColumnType.Simple);
        this.addColumn('Eviction Filed', BoardColumnType.Simple);
        this.addColumn('Hearing', BoardColumnType.Simple);
        this.addColumn('Judgment', BoardColumnType.Simple);
        this.addColumn('Appeal', BoardColumnType.Simple);
        this.addColumn('Writ Filed', BoardColumnType.Simple);
        this.addColumn('Writ Posted', BoardColumnType.Simple);
        this.addColumn('Writ Executed', BoardColumnType.Simple);
        break;
      case BoardType.MoveIn:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Move in', BoardColumnType.MoveIn, false);
        this.addColumn('Application', BoardColumnType.Simple);
        this.addColumn('Set MI Date', BoardColumnType.Simple);
        this.addColumn('Lease signed', BoardColumnType.Simple);
        this.addColumn('Key prep', BoardColumnType.Simple);
        this.addColumn('Final Sparkle', BoardColumnType.Simple);
        this.addColumn('Key handover', BoardColumnType.Simple);
        this.addColumn('MI Inspection', BoardColumnType.Simple);
        this.addColumn('PMS MoveIn', BoardColumnType.Simple);
        break;
      case BoardType.MoveOut:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Move out', BoardColumnType.MoveOut, false);
        this.addColumn('Move in', BoardColumnType.MoveIn, false);
        this.addColumn('Inspection', BoardColumnType.Simple);
        this.addColumn('Bid', BoardColumnType.Simple);
        this.addColumn('Keys', BoardColumnType.Simple);
        this.addColumn('Deposit', BoardColumnType.Simple);
        this.addColumn('PMS MoveOut', BoardColumnType.Simple);
        this.addColumn('Marketing', BoardColumnType.Simple);
        break;
      case BoardType.Approval:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Move in', BoardColumnType.MoveIn, false);
        this.addColumn('Avail', BoardColumnType.Available, true);
        this.addColumn('Blue Tape', BoardColumnType.Simple);
        this.addColumn('Final Clean', BoardColumnType.Simple);
        break;
      case BoardType.Custom:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn(isProjectBoard ? 'Start Date' : 'Move out', BoardColumnType.MoveOut, false);
        this.addColumn('Anything', BoardColumnType.Simple);
        this.addColumn('your', BoardColumnType.Simple);
        this.addColumn('heart', BoardColumnType.Simple);
        this.addColumn('desires!', BoardColumnType.Simple);
        break;
      case BoardType.LeaseUp:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Pics done', BoardColumnType.Simple);
        this.addColumn('ILS Posted', BoardColumnType.Simple);
        break;
      case BoardType.Prelease:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Finish', BoardColumnType.NewFinish, false);
        this.addColumn('Total Days', BoardColumnType.TotalDays, false);
        this.addColumn('Adv.Rent', BoardColumnType.AdvertisedRent, false);
        this.addColumn('Market Rent', BoardColumnType.MarketRent, false);
        this.addColumn('Lease Signed', BoardColumnType.Simple);
        this.addColumn('Deposit', BoardColumnType.Simple);
        this.addColumn('Forms Complete', BoardColumnType.Simple);
        this.addColumn('Sparkle', BoardColumnType.Simple);
        this.addColumn('Key hand off', BoardColumnType.Simple);
        break;
      default:
        this.addColumn('Conversation', BoardColumnType.Comments);
        this.addColumn('Trash out', BoardColumnType.Simple);
        this.addColumn('Hardware', BoardColumnType.Simple);
        break;
    }
  }
}

export type Sorting = 'ascending' | 'descending' | null;

export class ColumnSorting {
  columnId: string;
  direction: Sorting;
  sortFunction: (e1: RestTurnoverTaskModel, e2: RestTurnoverTaskModel) => number;

  constructor(column: BoardColumn, sorting: Sorting, primaryValue: boolean, private cellValueHelper: CellValueHelper) {
    this.columnId = column.id;
    this.direction = sorting;
    const fieldFunction = this.getComparedValueFunction(column, this.columnId, primaryValue);
    this.sortFunction =
      fieldFunction === null
        ? null
        : (e1, e2) => {
            const isUnitOnHold1 = e1.turnoverData?.isUnitOnHold ?? false;
            const isUnitOnHold2 = e2.turnoverData?.isUnitOnHold ?? false;
            const isUnitDown1 = e1.turnoverData?.isUnitDown ?? false;
            const isUnitDown2 = e2.turnoverData?.isUnitDown ?? false;

            if (isUnitOnHold1 !== isUnitOnHold2) {
              return isUnitOnHold1 ? 1 : -1;
            }

            // rules for null sorting
            if (sorting === null) {
              if (isUnitOnHold1 !== isUnitOnHold2) {
                return isUnitOnHold1 ? 1 : -1;
              }

              if (isUnitDown1 !== isUnitDown2) {
                return isUnitDown1 ? 1 : -1;
              }
              return 0;
            }

            // isUnitDown logic works for null sorting and does not work for sorting by units
            const isUnitSortingWithPrimary = sorting && column.type === BoardColumnType.Unit && primaryValue;
            if (!isUnitSortingWithPrimary && isUnitDown1 !== isUnitDown2) {
              return isUnitDown1 ? 1 : -1;
            }

            if (sorting && column.type === BoardColumnType.Unit) {
              const buildingNumber1 = e1.buildingNumber;
              const buildingNumber2 = e2.buildingNumber;
              if (buildingNumber1 && buildingNumber2)
                return sorting === 'ascending' ? buildingNumber1 - buildingNumber2 : buildingNumber2 - buildingNumber1;

              const unitName1 = e1.unitName?.toLowerCase() || '';
              const unitName2 = e2.unitName?.toLowerCase() || '';
              return sorting === 'ascending' ? unitName1.localeCompare(unitName2) : unitName2.localeCompare(unitName1);
            }

            const value1 = fieldFunction(e1);
            const value2 = fieldFunction(e2);

            // empty cells of the isUnitDown units
            if (value1 === 0 && isUnitDown1 && !(value2 === 0 && isUnitDown2)) {
              return 1;
            } else if (!(value1 === 0 && isUnitDown1) && value2 === 0 && isUnitDown2) {
              return -1;
            }

            // empty cells of the !isUnitDown units
            if (column.type !== BoardColumnType.CustomAmount) {
              if (value1 === 0 && value2 !== 0) {
                return 1;
              } else if (value1 !== 0 && value2 === 0) {
                return -1;
              } else if (value1 === 0 && value2 === 0) {
                return 0;
              }
            }

            // NOT empty cells of the isUnitDown units
            if (isUnitDown1 !== isUnitDown2) {
              return isUnitDown1 ? 1 : -1;
            }

            // TurnDescription logic
            if (column.type === BoardColumnType.TurnDescription || column.type === BoardColumnType.Notes) {
              const isEmpty1 = value1 === null || value1 === undefined || value1 === '';
              const isEmpty2 = value2 === null || value2 === undefined || value2 === '';

              if (isEmpty1 && !isEmpty2) return 1;
              if (!isEmpty1 && isEmpty2) return -1;
              if (isEmpty1 && isEmpty2) return 0;

              return (value1 > value2 ? 1 : -1) * (sorting === 'ascending' ? 1 : -1);
            }

            // general rules of sorting
            if (value1 !== value2) {
              return (value1 > value2 ? 1 : -1) * (sorting === 'ascending' ? 1 : -1);
            }

            return 0;
          };
  }

  private getComparedValueFunction(
    column: BoardColumn,
    columnId: string,
    primaryValue: boolean
  ): (e: RestTurnoverTaskModel) => string | number {
    return primaryValue ? e => this.cellValueHelper.getComparableValue(column, e, columnId) : e => e.phasePriority;
  }
}

export class ColumnFilter {
  columnId: string;
  status: ScheduleCellStatus;

  constructor(columnId: string, status: ScheduleCellStatus) {
    this.columnId = columnId;
    this.status = status;
  }
}

export class BoardColumn {
  id: string;
  editable: boolean;
  name: string;
  type: BoardColumnType;
  workflowStep?: WorkflowStepEnumType;
  inspectionTemplateId?: number;
  dynamicTicketTypeId?: number;
  frozen = false;
  visible = true;
  options?: DropdownColumnOption[];
  colorBg?: string;
  colorText?: string;
  animate?: boolean;
  highlightedRow?: number[] | null;
  dropdownMultiCell?: boolean;

  constructor(config: Partial<BoardColumn>) {
    Object.assign(this, config);
    this.id = this.id ?? uuidv4();
  }
}

function isBoardColumn(entity: any): entity is BoardColumn {
  return typeof entity.name === 'string' && typeof entity.type === 'string';
}

export const isColumnSummable = (column: BoardColumn): boolean => {
  return [
    BoardColumnType.CustomAmount,
    BoardColumnType.Simple,
    BoardColumnType.UnitTotal,
    BoardColumnType.Budget,
    BoardColumnType.Delta,
  ].includes(column.type);
};

export const isCostColumn = (column: BoardColumn): boolean => {
  return [BoardColumnType.CustomAmount, BoardColumnType.Simple].includes(column.type);
};

export const isCalculatedValue = (column: BoardColumn): boolean => {
  return [BoardColumnType.UnitTotal, BoardColumnType.Delta, BoardColumnType.Budget].includes(column.type);
};

export const ProgressTypeList: IRadioButtonOption<BoardProgressType>[] = [
  {
    label: 'N/A',
    value: BoardProgressType.None,
  },
  {
    label: 'MAKE READY',
    value: BoardProgressType.UpdateMakeReadyProgressBar,
  },
  {
    label: 'RENO',
    value: BoardProgressType.UpdateRENOProgressBar,
  },
  {
    label: 'NOTICE',
    value: BoardProgressType.UpdateNoticeProgressBar,
  },
  {
    label: 'MOVE IN',
    value: BoardProgressType.UpdateMoveInProgressBar,
  },
  {
    label: 'MOVE OUT',
    value: BoardProgressType.UpdateMoveOutProgressBar,
  },
  {
    label: 'APPROVAL',
    value: BoardProgressType.UpdateApprovalProgressBar,
  },
  {
    label: 'AVAILABLE',
    value: BoardProgressType.UpdateLeaseUpProgressBar,
  },
];

export const BoardSourceTypeList: IRadioButtonOption<BoardSourceType>[] = [
  {
    label: 'MAKE READY',
    value: BoardSourceType.MakeReadyUnits,
  },
  {
    label: 'RENO',
    value: BoardSourceType.RenoUnits,
  },
  {
    label: 'Unleased',
    value: BoardSourceType.UnleasedUnits,
  },
  {
    label: 'NOTICE',
    value: BoardSourceType.OnNoticeUnits,
  },
  {
    label: 'MOVE OUT',
    value: BoardSourceType.MoveOutUnits,
  },
  {
    label: 'NOTICE and Unleased',
    value: BoardSourceType.OnNoticeAndUnleasedUnits,
  },
  {
    label: 'NOTICE under Eviction',
    value: BoardSourceType.UnitsInNoticeUnderEviction,
  },
  {
    label: 'APPROVAL',
    value: BoardSourceType.UnitsInApproval,
  },
  {
    label: 'AVAILABLE',
    value: BoardSourceType.LeaseUpUnits,
  },
  {
    label: 'Prelease',
    value: BoardSourceType.PreleaseUnits,
  },
  {
    label: 'MOVE IN',
    value: BoardSourceType.UnitsInMoveIn,
  },
  {
    label: 'PUNCH',
    value: BoardSourceType.UnitsInPunch,
  },
  {
    label: 'Non-PMS',
    value: BoardSourceType.NonPmsUnits,
  },
  {
    label: 'N/A',
    value: BoardSourceType.Manual,
  },
  {
    label: 'Unleased (Not Ready)',
    value: BoardSourceType.UnleasedUnitsNotReady,
  },
  {
    label: 'Unleased (Ready)',
    value: BoardSourceType.UnleasedUnitsReady,
  },
];

export const BoardTypeList: IRadioButtonOption<BoardType>[][] = [
  [
    {
      label: 'Make Ready',
      value: BoardType.MakeReady,
    },
    {
      label: 'Reno',
      value: BoardType.Reno,
    },
    {
      label: 'Marketing',
      value: BoardType.Marketing,
    },
    {
      label: 'Available',
      value: BoardType.LeaseUp,
    },
    {
      label: 'Prelease',
      value: BoardType.Prelease,
    },
    {
      label: 'Eviction',
      value: BoardType.Eviction,
    },
    {
      label: 'Move In',
      value: BoardType.MoveIn,
    },
    {
      label: 'Move Out',
      value: BoardType.MoveOut,
    },
    {
      label: 'Approval',
      value: BoardType.Approval,
    },
    {
      label: 'Non-PMS',
      value: BoardType.NonPms,
    },
  ],
  [
    {
      label: 'Custom',
      value: BoardType.Custom,
    },
  ],
];

export const BoardLayoutTypeList: IRadioButtonOption<BoardLayoutType>[] = [
  {
    label: 'Turnover Board',
    value: BoardLayoutType.TurnoverBoard,
  },
  {
    label: 'Project Board',
    value: BoardLayoutType.ProjectsBoard,
  },
];

export const BoardOutputBehaviourList: IRadioButtonOption<BoardOutputBehaviour>[] = [
  {
    label: 'Automatically',
    value: BoardOutputBehaviour.Automatically,
  },
  {
    label: 'Manually',
    value: BoardOutputBehaviour.Manually,
  },
];

export const DONT_CREATE_TICKETS = -1;
export const DynamicTicketTypeDefault: IRadioButtonOption<number | null>[] = [
  {
    label: "Don't create tickets",
    value: DONT_CREATE_TICKETS,
  },
];

export const PoNumberIsAvailableRule: IRadioButtonOption<boolean>[] = [
  {
    label: 'Don’t show',
    value: false,
  },
  {
    label: 'Display on Schedule',
    value: true,
  },
];

export const BoardOutputTypeList: IRadioButtonOption<BoardOutputType>[] = [
  {
    label: 'N/A',
    value: BoardOutputType.None,
  },
  {
    label: 'APPROVAL',
    value: BoardOutputType.SendToApproval,
  },
  {
    label: 'AVAILABLE/MOVE IN',
    value: BoardOutputType.SendToLeaseUpOrMoveIn,
  },
  {
    label: 'MOVE OUT',
    value: BoardOutputType.SendToMoveOut,
  },
  {
    label: 'MAKE READY',
    value: BoardOutputType.SendToMakeReady,
  },
  {
    label: 'PUNCH',
    value: BoardOutputType.SendToPunch,
  },
  {
    label: 'RENO',
    value: BoardOutputType.SendToReno,
  },
  {
    label: 'ARCHIVE',
    value: BoardOutputType.SendToArchive,
  },
];

export const ProjectBoardOutputTypeList: IRadioButtonOption<BoardOutputType>[] = [
  {
    label: 'N/A',
    value: BoardOutputType.None,
  },
  {
    label: 'ARCHIVE',
    value: BoardOutputType.SendToArchive,
  },
];

export const disallowSourceTypeOutputTypeMap: Record<number, number> = {
  [BoardSourceType.MakeReadyUnits]: BoardOutputType.SendToMakeReady,
  [BoardSourceType.UnitsInPunch]: BoardOutputType.SendToPunch,
  [BoardSourceType.RenoUnits]: BoardOutputType.SendToReno,
  [BoardSourceType.MoveOutUnits]: BoardOutputType.SendToMoveOut,
  [BoardSourceType.UnitsInMoveIn]: BoardOutputType.SendToLeaseUpOrMoveIn,
  [BoardSourceType.UnitsInApproval]: BoardOutputType.SendToApproval,
};

export function getNextStepType(outputAction: BoardOutputType, turnover: RestTurnoverTaskModel): WorkflowStepEnumType {
  switch (outputAction) {
    case BoardOutputType.SendToLeaseUpOrMoveIn:
      return !!turnover.turnoverData.dateMoveIn ? WorkflowStepEnumType.MoveIn : WorkflowStepEnumType.LeaseUp;
    case BoardOutputType.SendToApproval:
      return WorkflowStepEnumType.Approval;
    case BoardOutputType.SendToMoveOut:
      return WorkflowStepEnumType.MoveOut;
    case BoardOutputType.SendToReno:
      return WorkflowStepEnumType.Reno;
    case BoardOutputType.SendToMakeReady:
      return WorkflowStepEnumType.MakeReady;
    case BoardOutputType.SendToPunch:
      return WorkflowStepEnumType.Punch;
    case BoardOutputType.SendToArchive:
      return WorkflowStepEnumType.Archive;
    case BoardOutputType.None:
      return null;
    default:
      return WorkflowStepEnumType.Approval;
  }
}

export function getCurrentStep(sourceType: BoardSourceType, skipDefault = false): WorkflowStepEnumType {
  switch (sourceType) {
    case BoardSourceType.MakeReadyUnits:
      return WorkflowStepEnumType.MakeReady;
    case BoardSourceType.RenoUnits:
      return WorkflowStepEnumType.Reno;
    case BoardSourceType.OnNoticeUnits:
      return WorkflowStepEnumType.Notice;
    case BoardSourceType.MoveOutUnits:
      return WorkflowStepEnumType.MoveOut;
    case BoardSourceType.MarketingUnits:
      return WorkflowStepEnumType.Marketing;
    case BoardSourceType.OnNoticeAndUnleasedUnits:
      return WorkflowStepEnumType.Notice;
    case BoardSourceType.UnitsInNoticeUnderEviction:
      return WorkflowStepEnumType.Notice;
    case BoardSourceType.UnitsInMoveIn:
      return WorkflowStepEnumType.MoveIn;
    case BoardSourceType.UnitsInApproval:
      return WorkflowStepEnumType.Approval;
    case BoardSourceType.UnitsInPunch:
      return WorkflowStepEnumType.Punch;
    default:
      return skipDefault ? null : WorkflowStepEnumType.MoveOut;
  }
}

export interface BoardFilter {
  searchStr?: string;
  events?: EventActivityFilter[];
  types?: string[];
  finish?: number[];
  assignees?: number[];
  turnAssignees?: number[];
  properties?: number[];
  schedule?: string[];
  status?: ScheduleCellStatus[];
  turnStepScheduleStatus?: TurnStepScheduleStatus[];
  boards?: number[];

  //Not used but keeped controls
  startMoveInDate?: Date;
  endMoveInDate?: Date;
  startMoveOutDate?: Date;
  endMoveOutDate?: Date;
  startAvailableDate?: Date;
  endAvailableDate?: Date;
  startShowableDate?: Date;
  endShowableDate?: Date;

  unitTypes?: string[];
  unitStatus?: string[];

  timeframe?: number;
  units?: number[];
  columnNames?: string[];
  queryType?: QueryType;
  boardType?: BoardType;
  boardId?: number;
  projectTypes?: number[];

  scheduleByStatusFilter?: ScheduleByStatusFilter;

  startDate?: Date;
  endDate?: Date;
}

export class BoardEvent {
  name: string;
  id: string;
  colorScheme: number;
}

export interface EventActivityFilter {
  eventType: number;
  label: string;
  colorScheme: number;
  value?: any;
}

export interface DeleteUnitFromBoardI {
  unitName: string;
  id: number;
}

export interface BoardLayoutInspectionTemplatesUsage {
  boardLayoutId: number;
  columnId: string;
  inspectionTemplateId: number;
  pushToPMS: boolean;
  ticketTopicId?: number;
}

export interface ColumnStats {
  key: ScheduleCellStatus;
  value: number;
}

export interface BoardChangesNotification {
  commentId: number;
  turnoverId: number;
  unitId: number;
  propertyId: number;
  unitName: string;
  isNonPms: boolean;
  propertyName: string;
  commentType: number;
  commentCreationDate: Date;
  commentText: string;
  authorId: number;
  isRead?: boolean;
  unitIconType: string;
}

export interface BoardCustomNotificationPreset {
  id: number;
  name: string;
  presetType: number;
  projectTypeIds: string;
  notifyAssigneeMode: number;
  notifyAssigneeOn: Date;
  notifyAssigneeOnCustom: number;
  notifyAssigneeOnCustomBBDateFieldIdReference: string;
  notifyAssigneeAgainMode: number;
  notifyAssigneeAgainOn: Date;
  notifyAssigneeAgainOnCustom: number;
  notifyAssigneeAgainOnCustomBBDateFieldIdReference: string;
  remindAssigneeEveryNDays: number;
  remindAssigneeStopAfterNReminders: number;
  createdAt: Date;
  createdBy: number;
  modifiedAt: Date;
  modifiedBy: number;
  isDeleted: boolean;
  deletedAt: Date;
  deletedBy: number;
}

export enum BoardCustomNotificationPresetMode {
  DontNotify = 0,
  SpecificDate = 1,
  Calculated = 2,
}

export enum BoardCustomNotificationPresetType {
  Any = 0,
  TurnsOnly = 1,
  AnyProjectType = 2,
  SpecificProjects = 3,
}

export interface BoardCustomNotification {
  id: number;
  notifyAssigneeMode?: BoardCustomNotificationPresetMode;
  notifyAssigneeOn?: Date | null;
  notifyAssigneeOnCustom?: number | null;
  notifyAssigneeOnCustomBBDateFieldIdReference?: string | null;
  notifyAssigneeAgainMode?: BoardCustomNotificationPresetMode | null;
  notifyAssigneeAgainOn?: Date | null;
  notifyAssigneeAgainOnCustom?: number | null;
  notifyAssigneeAgainOnCustomBBDateFieldIdReference?: string | null;
  remindAssigneeEveryNDays?: number | null;
  remindAssigneeStopAfterNReminders?: number | null;
  notifyAssigneeWhenStepCompletedOrChanged?: boolean;

  nextTimeSendANotificationOn?: Date | null;
  initialNotifyAssigneeState?: number;
}

export interface BoardCustomNotificationPresetCreate {
  name: string;
  presetType?: BoardCustomNotificationPresetType | null;
  projectTypeIds?: string | null;
  notifyAssigneeMode: BoardCustomNotificationPresetMode;
  notifyAssigneeOn?: Date | null;
  notifyAssigneeOnCustom?: number | null;
  notifyAssigneeOnCustomBBDateFieldIdReference?: string | null;
  notifyAssigneeAgainMode?: BoardCustomNotificationPresetMode | null;
  notifyAssigneeAgainOn?: Date | null;
  notifyAssigneeAgainOnCustom?: number | null;
  notifyAssigneeAgainOnCustomBBDateFieldIdReference?: string | null;
  remindAssigneeEveryNDays?: number | null;
  remindAssigneeStopAfterNReminders?: number | null;
}

export interface BoardCustomNotificationPresetUpdate extends BoardCustomNotificationPresetCreate {
  id: number;
}
